import { AfterViewInit, Component, ElementRef, OnDestroy, ViewChild } from '@angular/core';
import { ReplaySubject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

import { Loggable } from 'app/utils/logging/loggable';
import { notNil } from 'app/utils/stream-util';

import { DeviceService } from '../../services/device/device.service';

@Component({
  selector: 'media-debugger',
  templateUrl: './media-debugger.component.html',
  styleUrls: ['./media-debugger.component.scss'],
  standalone: false,
})
export class MediaDebuggerComponent extends Loggable implements OnDestroy, AfterViewInit {
  @ViewChild('previewVideo') private previewVideo?: ElementRef<HTMLVideoElement>;
  @ViewChild('rawPreviewVideo') private rawPreviewVideo?: ElementRef<HTMLVideoElement>;
  @ViewChild('hdPreviewVideo') private hdPreviewVideo?: ElementRef<HTMLVideoElement>;

  private destroyed$ = new ReplaySubject<boolean>(1);

  public constructor(private deviceService: DeviceService) {
    super();
    this.setDisplayName('MediaDebuggerComponent');
  }

  public ngAfterViewInit(): void {
    this.deviceService.cameraStream$
      .pipe(filter(notNil))
      .pipe(takeUntil(this.destroyed$))
      .subscribe(this.updateCameraStream.bind(this));

    this.deviceService.rawCameraStream$
      .pipe(filter(notNil))
      .pipe(takeUntil(this.destroyed$))
      .subscribe(this.updateRawCameraStream.bind(this));

    this.deviceService.hdCameraStream$
      .pipe(filter(notNil))
      .pipe(takeUntil(this.destroyed$))
      .subscribe(this.updateHdCameraStream.bind(this));
  }

  private updateCameraStream(stream: MediaStream): void {
    this.info('camera stream changed', stream.id);
    if (!this.previewVideo) {
      return;
    }
    this.previewVideo.nativeElement.srcObject = stream;
    this.previewVideo.nativeElement.oncanplay = () => {
      this.previewVideo?.nativeElement.play().catch((e) => {
        this.error('Error playing camera stream', e);
      });
      this.info('Playing camera stream', 'oncanplay');
    };
  }

  private updateRawCameraStream(stream: MediaStream): void {
    this.info('raw camera stream changed', stream.id);
    if (!this.rawPreviewVideo) {
      return;
    }
    this.rawPreviewVideo.nativeElement.srcObject = stream;
    this.rawPreviewVideo.nativeElement.oncanplay = () => {
      this.previewVideo?.nativeElement.play().catch((e) => {
        this.error('Error playing raw camera stream', e);
      });
      this.info('Playing raw camera stream', 'oncanplay');
    };
  }

  private updateHdCameraStream(stream: MediaStream): void {
    this.info('hd camera stream changed', stream.id);
    if (!this.hdPreviewVideo) {
      return;
    }
    this.hdPreviewVideo.nativeElement.srcObject = stream;
    this.hdPreviewVideo.nativeElement.oncanplay = () => {
      this.previewVideo?.nativeElement.play().catch((e) => {
        this.error('Error playing hd camera stream', e);
      });
      this.info('Playing hd camera stream', 'oncanplay');
    };
  }

  public ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }
}
